// libs
import React from 'react';
// services
import { API_FILE_TYPE, CROP_SIZES } from '../../../constants';
// constants
import { useAsyncValue } from 'framework/utils';
import { InputLoader } from 'framework/ui/mui-builder';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import { getFileTypeIcon } from '../../../utils';
import { Box, IconButton } from '@mui/material';
import { keyframes } from '@mui/material/styles';

const artifactPreviewSpinner = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const isImage = (artifact: Dictionary<any>) => artifact && artifact.type && artifact.type === API_FILE_TYPE.image.alias;

type FilePreviewLayoutProps = {
  artifactObject: Dictionary<any>;
  getImageThumbnail: (artifact: Dictionary<any>, cropSize: string) => Promise<string | null>;
  onRemove: () => void;
  children?: Nullable<React.ReactElement>;
  disabled?: boolean;
};

const BaseFilePreviewLayout = ({
  artifactObject,
  getImageThumbnail,
  onRemove,
  children = null,
  disabled = false,
}: FilePreviewLayoutProps) => {
  const getSrc = async () => {
    let src;

    if (isImage(artifactObject)) {
      src = await getImageThumbnail(artifactObject, CROP_SIZES.SIZE_100);
    }

    if (!src) {
      src = getFileTypeIcon(artifactObject.type || API_FILE_TYPE.default.alias);
    }

    return src;
  };

  const src = useAsyncValue(getSrc);

  return (
    <Box
      className="photo-preview"
      data-testid="file-preview"
      sx={theme => ({
        width: '100px',
        height: '100px',
        position: 'relative',
        border: '1px solid',
        borderColor: artifactObject.invalid ? 'error.main' : 'border.default',
        [theme.breakpoints.down('smallMobile')]: {
          width: '50px',
          height: '50px',
        },
        ...(artifactObject.uploading && {
          borderColor: 'transparent',
          '& img': {
            opacity: 0.3,
            transition: 'opacity 0.2s',
          },
          '&::after': {
            content: '""',
            boxSizing: 'border-box',
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '20px',
            height: '20px',
            marginTop: '-10px',
            marginLeft: '-10px',
            borderRadius: '50%',
            borderTop: '2px solid var(--mui-palette-primary-main)',
            borderRight: '2px solid transparent',
            animation: `${artifactPreviewSpinner} 0.6s linear infinite`,
          },
        }),
      })}
    >
      {src ? (
        <Box
          component="img"
          src={src}
          alt={artifactObject.name}
          title={artifactObject.title || artifactObject.name}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <InputLoader />
      )}
      <IconButton
        disabled={disabled}
        aria-label="Remove artifact"
        onClick={onRemove}
        sx={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          position: 'absolute',
          top: '-5px',
          right: '-5px',
          padding: 0,
          backgroundColor: disabled ? 'border.medium' : 'error.main',
          border: 0,
          color: disabled ? 'text.secondary' : 'common.white',
          borderRadius: '50%',
          fontSize: '12px',
          width: '20px',
          height: '20px',
          '&:hover': { backgroundColor: disabled ? 'border.medium' : 'error.main' },
          '&.Mui-disabled': { color: 'text.secondary' },
        }}
      >
        <CloseIcon sx={{ fontSize: '14px' }} />
      </IconButton>
      {children}
    </Box>
  );
};

export default BaseFilePreviewLayout;
